import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { fetchRecordedEntryPath } from '@/lib/auth/entryPath';
import { useAuth } from '@/contexts/AuthContext';
import { CURRENT_BETA_AGREEMENT_VERSION } from '@/legal/versions';
import {
  CURRENT_PRIVACY_VERSION,
  CURRENT_SHOP_TOS_VERSION,
  CURRENT_PASSPORT_TOS_VERSION,
  CURRENT_AGE_VERIFICATION_VERSION,
} from '@/legal/versions';

export function useBetaSigningGate() {
  const { user, activeShop } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['beta-signing-gate', user?.id, activeShop?.id],
    enabled: !!user?.id && !!activeShop?.id,
    queryFn: async () => {
      // Check if shop is a beta tester
      const { data: shop } = await supabase
        .from('shops')
        .select('is_beta_tester, is_founding_beta')
        .eq('id', activeShop!.id)
        .single();

      if (!shop?.is_beta_tester && !shop?.is_founding_beta) {
        return { needsSigning: false, needsLegalReview: false, isBetaShop: false };
      }

      // Check if user has already signed the beta agreement
      const { data: acceptance } = await supabase
        .from('legal_acceptances')
        .select('id')
        .eq('user_id', user!.id)
        .eq('agreement_type', 'beta_agreement_signed')
        .eq('agreement_version', CURRENT_BETA_AGREEMENT_VERSION)
        .maybeSingle();

      if (!acceptance) {
        return { needsSigning: true, needsLegalReview: false, isBetaShop: true };
      }

      // Beta agreement is signed — now check for privacy, TOS, and age verification
      // Core-100 R2 (CORE-A): the entry path as the server recorded it at sign-up, not the editable metadata.
      const accountType = await fetchRecordedEntryPath(user!.id);
      const tosType = accountType === 'passport' ? 'tos_passport' : 'tos_shop';
      const tosVersion = accountType === 'passport' ? CURRENT_PASSPORT_TOS_VERSION : CURRENT_SHOP_TOS_VERSION;

      const { data: legalAcceptances } = await supabase
        .from('legal_acceptances')
        .select('agreement_type')
        .eq('user_id', user!.id)
        .in('agreement_type', ['privacy_policy', tosType, 'age_verification']);

      const acceptedTypes = new Set(
        (legalAcceptances ?? []).map((r) => r.agreement_type)
      );

      // We also need to verify the versions are current — query with version filters
      const checks = await Promise.all([
        supabase
          .from('legal_acceptances')
          .select('id')
          .eq('user_id', user!.id)
          .eq('agreement_type', 'privacy_policy')
          .eq('agreement_version', CURRENT_PRIVACY_VERSION)
          .maybeSingle(),
        supabase
          .from('legal_acceptances')
          .select('id')
          .eq('user_id', user!.id)
          .eq('agreement_type', tosType)
          .eq('agreement_version', tosVersion)
          .maybeSingle(),
        supabase
          .from('legal_acceptances')
          .select('id')
          .eq('user_id', user!.id)
          .eq('agreement_type', 'age_verification')
          .eq('agreement_version', CURRENT_AGE_VERIFICATION_VERSION)
          .maybeSingle(),
      ]);

      const allPresent = checks.every((c) => c.data != null);

      return {
        needsSigning: false,
        needsLegalReview: !allPresent,
        isBetaShop: true,
      };
    },
    staleTime: 5 * 60 * 1000,
  });

  return {
    needsSigning: data?.needsSigning ?? false,
    needsLegalReview: data?.needsLegalReview ?? false,
    isBetaShop: data?.isBetaShop ?? false,
    isLoading,
  };
}
